import { isEqual } from 'lodash'
import { getListDataFunctionFactory } from './getListDataFunctionFactory'
import type { TEntityDocument } from '~/types/document/models'
import type { TEntityId } from '~/types/models'

const mn = '[api/msw/modules/documentMockDataFactory.ts]'

export function makeDocumentMockData<T extends TEntityDocument>({
  entityName,
  storageKeyPrefix,
  getStartingListData = async () => [] as unknown as T[],
}: {
  entityName: string
  storageKeyPrefix: string
  /**
   * Seed rows for this collection. Optional, and empty by default, which is what
   * every collection wanted until Cases: documents are created by the user, so
   * starting empty was the whole story.
   *
   * A case is different -- its documents also arrive from the email pipeline, and the
   * cases that matter (an ingested inbound body, a reply whose delivery
   * BOUNCED) cannot be produced by clicking. Without seed rows the mock is
   * narrower than the server and that behaviour is untestable, which is a
   * failure mode this project has paid for repeatedly.
   */
  getStartingListData?: () => Promise<T[]>
}) {
  const documentListData = undefined as unknown as T[]

  const getDocumentListData = getListDataFunctionFactory<T[]>({
    listData: documentListData,
    storageKey: `${storageKeyPrefix}documents`,
    getStartingListData,
  })

  async function getDocumentDataById(id: TEntityId): Promise<T> {
    const fn = `${mn} getDocumentDataById |`
    const list = await getDocumentListData()
    if (!list) {
      throw new Error(`${fn} returned undefined/null`)
    }
    const d = list.find((document: T) => document.id === id)
    if (!d) {
      throw new Error(`${fn} Can't find ${entityName}Document '${id}'`)
    }
    return d
  }

  async function getDocumentListDataByParentId(id: TEntityId) {
    const fn = `${mn} getDocumentListDataByParentId |`
    const list: T[] = await getDocumentListData()
    if (!list) {
      throw new Error(`${fn} (${entityName}Documents) returned undefined/null`)
    }
    const filteredList = list.filter((document: TEntityDocument) => {
      console.debug(`${fn} ${entityName}, ${document[entityName].id}, ${id}`)
      return isEqual(document[entityName].id, id)
    })
    return filteredList?.length > 0 ? filteredList : []
  }

  return {
    getDocumentListData,
    getDocumentDataById,
    getDocumentListDataByParentId,
  }
}
